import React, { Component } from 'react';
import { Phone, Droplet, ThermometerSun, AlertTriangle, CheckCircle } from 'lucide-react';
export function AutoCoolingSystem() {
  return <div className="min-h-screen bg-gray-50" data-id="element-712">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-713">
        <div className="container mx-auto px-4" data-id="element-714">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-715">
            Auto Cooling System Repair
          </h1>
          <p className="text-xl text-gray-100" data-id="element-716">
            Expert Cooling System Service and Maintenance
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-717">
        <div className="max-w-4xl mx-auto" data-id="element-718">
          {/* The Need for a Cooling System */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-719">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-720">
              <ThermometerSun className="mr-3 text-[#B1050B]" size={32} data-id="element-721" />
              The Need for a Cooling System
            </h2>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-6" data-id="element-722">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-723">
                Proper Coolant Mixture
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-724">
                When the cooling system is refilled, use a{' '}
                <strong data-id="element-725">
                  50/50 mixture of ethylene glycol antifreeze and water
                </strong>
                . This will give freezing protection down to -34 degrees
                Fahrenheit, and boiling protection to 265 degrees F. in a
                pressurized system with a 14-psi radiator cap.
              </p>
              <p className="text-gray-700" data-id="element-726">
                A <strong data-id="element-727">70/30 mixture</strong> will protect against freezing
                down to -84 degrees F. and boilover up to 276 degrees F. Do not
                use more than 70% antifreeze because antifreeze carries heat
                less efficiently than water. Straight water should never be used
                in the cooling system because it offers no boilover or freezing
                protection and no corrosion protection (which extremely
                important today's bimetal and aluminum engines).
              </p>
            </div>
          </div>

          {/* How the Cooling System Works */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-728">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-729">
              How the Cooling System Functions
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-730">
              The cooling system of a vehicle operates or functions by directing
              liquid coolant via passages in the engine block and heads. After
              that the liquid coolant which flows through passages absorb the
              heat from an engine. Then this fluid passes through a rubber hose
              to reach to the radiator. At this stage the hot liquid is cooled
              by air stream entering the engine compartment via grills. Once it
              gets cooled, then it goes back to engine and same process repeats
              again.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-731">
              Key Components of the Cooling System:
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6" data-id="element-732">
              <ul className="space-y-2 text-gray-700" data-id="element-733">
                <li className="flex items-start" data-id="element-734">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-735" />
                  <span data-id="element-736">Pressure cap</span>
                </li>
                <li className="flex items-start" data-id="element-737">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-738" />
                  <span data-id="element-739">Heater hoses</span>
                </li>
                <li className="flex items-start" data-id="element-740">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-741" />
                  <span data-id="element-742">Thermostat</span>
                </li>
                <li className="flex items-start" data-id="element-743">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-744" />
                  <span data-id="element-745">Heater core</span>
                </li>
                <li className="flex items-start" data-id="element-746">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-747" />
                  <span data-id="element-748">Reserve tank</span>
                </li>
                <li className="flex items-start" data-id="element-749">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-750" />
                  <span data-id="element-751">Lower hose</span>
                </li>
              </ul>
              <ul className="space-y-2 text-gray-700" data-id="element-752">
                <li className="flex items-start" data-id="element-753">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-754" />
                  <span data-id="element-755">Transmission cooler</span>
                </li>
                <li className="flex items-start" data-id="element-756">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-757" />
                  <span data-id="element-758">Water pump</span>
                </li>
                <li className="flex items-start" data-id="element-759">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-760" />
                  <span data-id="element-761">Fan</span>
                </li>
                <li className="flex items-start" data-id="element-762">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-763" />
                  <span data-id="element-764">Radiator</span>
                </li>
                <li className="flex items-start" data-id="element-765">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-766" />
                  <span data-id="element-767">Upper hose</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Head Gasket Section */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-768">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-769">
              One of the most important parts of the cooling system is also the
              most invisible
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-770">
              The head gasket must maintain the seal around the combustion
              chamber at peak operating temperature and pressure. The gasket
              must seal against air, coolants, combustion and engine oil at
              their respective peak operating temperature and pressure. The
              materials used and design employed must be thermally and
              chemically resistant to the products of combustion and the various
              chemicals, coolants and oils used in the engine.
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6" data-id="element-771">
              <div className="flex items-start" data-id="element-772">
                <AlertTriangle className="text-yellow-600 mr-3 flex-shrink-0 mt-1" size={24} data-id="element-773" />
                <div data-id="element-774">
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-775">
                    Critical Component
                  </h3>
                  <p className="text-gray-700" data-id="element-776">
                    The head gasket is critical to the proper functioning of
                    your cooling system. A failed head gasket can cause coolant
                    leaks, overheating, and serious engine damage. If you
                    suspect head gasket problems, bring your vehicle in for
                    diagnosis immediately.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Solution Section */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-777">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-778">
              <Droplet className="mr-3 text-[#B1050B]" size={28} data-id="element-779" />
              Our Cooling System Service Solution
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-780">
              The entire cooling system is first cleaned and then all debris and
              worn out coolant is completely removed from the system. Then,
              fresh new premium coolant is added. Conditioners are also included
              to prevent rust and corrosion and BG Universal Cooling System
              Sealer is also included to prevent leaks.
            </p>

            <div className="bg-green-50 rounded-lg p-6" data-id="element-781">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-782">
                Our Service Includes:
              </h3>
              <ul className="space-y-3 text-gray-700" data-id="element-783">
                <li className="flex items-start" data-id="element-784">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-785" />
                  <span data-id="element-786">Complete cooling system cleaning</span>
                </li>
                <li className="flex items-start" data-id="element-787">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-788" />
                  <span data-id="element-789">Removal of all debris and worn out coolant</span>
                </li>
                <li className="flex items-start" data-id="element-790">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-791" />
                  <span data-id="element-792">Fresh premium coolant added</span>
                </li>
                <li className="flex items-start" data-id="element-793">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-794" />
                  <span data-id="element-795">Rust and corrosion prevention conditioners</span>
                </li>
                <li className="flex items-start" data-id="element-796">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-797" />
                  <span data-id="element-798">
                    BG Universal Cooling System Sealer to prevent leaks
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-799">
            <h2 className="text-3xl font-bold mb-4" data-id="element-800">
              Need Cooling System Service?
            </h2>
            <p className="text-xl mb-6" data-id="element-801">Give us a call if we can help</p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-802">
              <Phone className="mr-3" size={24} data-id="element-803" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-804">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}